/**
 * Route helper utilities for building workspace-scoped paths.
 *
 * These helpers ensure consistent URL generation throughout the app
 * and make it easy to refactor routes in the future.
 *
 * IMPORTANT: Always use these helpers instead of hardcoding paths.
 *
 * Canonical URL structure:
 * - /w/:clientSlug/* - Workspace routes
 * - /clients - Client list (no slug)
 * - /help, /admin/* - Platform routes
 */

/**
 * Build a path within a workspace's scope.
 *
 * @param clientSlug - The client's URL slug (e.g., "dallas-life")
 * @param parts - Path parts after the client slug (leading/trailing slashes are stripped)
 * @returns Full path like "/w/dallas-life/campaigns"
 *
 * @example
 * buildClientPath("dallas-life") // "/w/dallas-life"
 * buildClientPath("dallas-life", "campaigns") // "/w/dallas-life/campaigns"
 * buildClientPath("dallas-life", "campaigns", "123") // "/w/dallas-life/campaigns/123"
 *
 * @deprecated Use buildWorkspacePath instead for clearer naming
 */
export function buildClientPath(
  clientSlug: string,
  ...parts: (string | undefined)[]
): string {
  const cleanParts = parts
    .filter((p): p is string => typeof p === "string" && p.length > 0)
    .map((p) => p.replace(/^\/+|\/+$/g, "")); // Strip leading/trailing slashes

  return ["/w", clientSlug, ...cleanParts].join("/");
}

/**
 * Extract the current section from a client/workspace-scoped path.
 * Recognizes both /clients/ (legacy) and /w/ (new) prefixes.
 *
 * @param pathname - The current URL pathname
 * @returns The section after the client slug, or empty string if at root
 *
 * @example
 * extractClientSection("/clients/dallas-life/campaigns") // "campaigns"
 * extractClientSection("/w/dallas-life/campaigns/123") // "campaigns/123"
 * extractClientSection("/w/dallas-life") // ""
 */
export function extractClientSection(pathname: string): string {
  const match = pathname.match(/\/(?:clients|w)\/[^/]+\/(.+)/);
  return match?.[1] || "";
}

/**
 * Replace the client slug in a path while preserving the section.
 * Recognizes both /clients/ (legacy) and /w/ (new) prefixes.
 *
 * @param pathname - The current URL pathname
 * @param newClientSlug - The new client slug to use
 * @returns Updated path with new client slug
 *
 * @example
 * replaceClientSlug("/clients/dallas-life/campaigns", "another-client")
 * // "/clients/another-client/campaigns"
 * replaceClientSlug("/w/dallas-life/campaigns", "another-client")
 * // "/w/another-client/campaigns"
 */
export function replaceClientSlug(
  pathname: string,
  newClientSlug: string,
): string {
  return pathname.replace(/\/(?:clients|w)\/[^/]+/, (match) => {
    const prefix = match.startsWith("/w/") ? "/w" : "/clients";
    return `${prefix}/${newClientSlug}`;
  });
}

/**
 * Check if a pathname is within the client/workspace routes.
 * Recognizes both /clients/ (legacy) and /w/ (new) prefixes.
 */
export function isClientRoute(pathname: string): boolean {
  return pathname.startsWith("/clients/") || pathname.startsWith("/w/");
}

/**
 * Extract client slug from a client/workspace-scoped path.
 * Recognizes both /clients/ (legacy) and /w/ (new) prefixes.
 *
 * @param pathname - The current URL pathname
 * @returns The client slug, or null if not a client route
 */
export function extractClientSlug(pathname: string): string | null {
  const match = pathname.match(/\/(?:clients|w)\/([^/]+)/);
  return match?.[1] || null;
}

/**
 * Parse a pathname into segments (filters empty strings).
 *
 * @param pathname - The URL pathname to parse
 * @returns Array of path segments
 *
 * @example
 * parsePathSegments("/clients/dallas-life/campaigns") // ["clients", "dallas-life", "campaigns"]
 */
export function parsePathSegments(pathname: string): string[] {
  return pathname.split("/").filter(Boolean);
}

/**
 * Replace just the client slug segment in a path.
 * Uses segment parsing to avoid accidental string replacements.
 * Recognizes both /clients/ (legacy) and /w/ (new) prefixes.
 *
 * @param pathname - The current URL pathname
 * @param newSlug - The new client slug
 * @returns Updated path, or null if not a client route
 */
export function replaceClientSlugSafe(
  pathname: string,
  newSlug: string,
): string | null {
  const segments = parsePathSegments(pathname);

  // Must be /clients/:slug[/...] or /w/:slug[/...]
  if ((segments[0] !== "clients" && segments[0] !== "w") || !segments[1]) {
    return null;
  }

  segments[1] = newSlug;
  return "/" + segments.join("/");
}

/**
 * Get the path sections after the client slug.
 * Useful for preserving the current "section" when switching clients.
 * Recognizes both /clients/ (legacy) and /w/ (new) prefixes.
 *
 * @param pathname - The current URL pathname
 * @returns Array of segments after the client slug
 *
 * @example
 * getClientSections("/clients/dallas-life/campaigns/123") // ["campaigns", "123"]
 * getClientSections("/w/dallas-life/campaigns") // ["campaigns"]
 * getClientSections("/w/dallas-life") // []
 */
export function getClientSections(pathname: string): string[] {
  const segments = parsePathSegments(pathname);

  if (segments[0] !== "clients" && segments[0] !== "w") {
    return [];
  }

  // Return everything after the prefix and the slug
  return segments.slice(2);
}

/**
 * Valid top-level sections for client routes.
 * Used to determine which section to preserve when switching clients.
 */
const CLIENT_SECTIONS = new Set([
  "build",
  "run",
  "annual-giving",
  "campaigns",
  "cycle",
  "strategy",
  "major-donors",
  "systems",
  "audiences",
  "forecasting",
  "events",
  "intelligence",
  "reports",
  "settings",

  "donor-lab",
  "brand-profile",
  "assessment",
  "help",
]);

/**
 * Get the top-level section from a client path (drops deep IDs).
 * Designed for route-safe client switching - preserves the section but not entity-specific IDs.
 *
 * @param pathname - The current URL pathname
 * @returns The top-level section, or empty string if at client root
 *
 * @example
 * getTopLevelSection("/clients/dallas-life/campaigns/123/analytics") // "campaigns"
 * getTopLevelSection("/clients/dallas-life/events/abc/edit") // "events"
 * getTopLevelSection("/clients/dallas-life/settings") // "settings"
 * getTopLevelSection("/clients/dallas-life") // ""
 * getTopLevelSection("/clients/dallas-life/unknown-section") // "" (invalid section)
 */
export function getTopLevelSection(pathname: string): string {
  const sections = getClientSections(pathname);

  if (sections.length === 0) {
    return "";
  }

  const topSection = sections[0];

  // Only return recognized sections to avoid navigating to invalid routes
  if (CLIENT_SECTIONS.has(topSection)) {
    return topSection;
  }

  // Unknown section - return empty to navigate to client home
  return "";
}

/**
 * Canonicalize a client path by ensuring the correct client slug is used.
 * Recognizes both /clients/ (legacy) and /w/ (new) prefixes.
 *
 * Used by ClientSync to correct URL when slug doesn't match the current client.
 * Returns null if not a client route or no correction needed.
 *
 * @param pathname - The current URL pathname
 * @param correctSlug - The canonical slug for the current client
 * @returns Corrected path, or null if no correction needed
 *
 * @example
 * // URL slug doesn't match context client slug
 * canonicalizeClientPath("/clients/old-slug/campaigns", "correct-slug")
 * // "/clients/correct-slug/campaigns"
 *
 * // UUID in URL (will be corrected to slug)
 * canonicalizeClientPath("/w/abc123-uuid/campaigns", "dallas-life")
 * // "/w/dallas-life/campaigns"
 *
 * // Already correct
 * canonicalizeClientPath("/w/dallas-life/campaigns", "dallas-life")
 * // null (no correction needed)
 */
export function canonicalizeClientPath(
  pathname: string,
  correctSlug: string,
): string | null {
  const segments = parsePathSegments(pathname);

  // Must be /clients/:something[/...] or /w/:something[/...]
  if ((segments[0] !== "clients" && segments[0] !== "w") || !segments[1]) {
    return null;
  }

  // Already correct
  if (segments[1] === correctSlug) {
    return null;
  }

  // Needs correction
  segments[1] = correctSlug;
  return "/" + segments.join("/");
}

// =============================================================================
// CLIENT SETTINGS TABS (Unified Settings Page)
// =============================================================================

/**
 * Valid tab IDs for the unified Client Settings page.
 * Replaces the old config/settings split with a single page.
 */
export const CLIENT_SETTINGS_TABS = {
  PROFILE: "profile",
  DATA: "data",
  INTEGRATIONS: "integrations",
  BRAND: "brand",
  STORIES: "stories",
  ADMIN: "admin",
} as const;

export type ClientSettingsTabId =
  (typeof CLIENT_SETTINGS_TABS)[keyof typeof CLIENT_SETTINGS_TABS];

export function buildClientSettingsPath(
  clientSlug: string,
  tab?: ClientSettingsTabId,
): string {
  const base = buildWorkspacePath(clientSlug, "settings");
  if (!tab || tab === "profile") return base;
  return `${base}?tab=${tab}`;
}

// =============================================================================
// NEW NAVIGATION ROUTES (Workspace Shell Redesign)
// =============================================================================

/**
 * Build a platform-level path (not scoped to any workspace).
 *
 * @param segments - Path segments
 * @returns Full path like "/dashboard" or "/help"
 *
 * @example
 * buildPlatformPath("dashboard") // "/dashboard"
 * buildPlatformPath("settings", "account") // "/settings/account"
 */
export function buildPlatformPath(...segments: (string | undefined)[]): string {
  const cleanSegments = segments
    .filter((s): s is string => typeof s === "string" && s.length > 0)
    .map((s) => s.replace(/^\/+|\/+$/g, ""));

  return "/" + cleanSegments.join("/");
}

/**
 * Build a workspace-scoped path (canonical).
 *
 * This is the primary route builder for workspace routes.
 * Always uses /w/:clientSlug/...
 *
 * @param clientSlug - The client's URL slug (e.g., "dallas-life")
 * @param parts - Path parts after the client slug
 * @returns Full path like "/w/dallas-life/campaigns"
 *
 * @example
 * buildWorkspacePath("dallas-life") // "/w/dallas-life"
 * buildWorkspacePath("dallas-life", "campaigns") // "/w/dallas-life/campaigns"
 * buildWorkspacePath("dallas-life", "run", campaignId) // "/w/dallas-life/run/abc123"
 */
export function buildWorkspacePath(
  clientSlug: string,
  ...parts: (string | undefined)[]
): string {
  const cleanParts = parts
    .filter((p): p is string => typeof p === "string" && p.length > 0)
    .map((p) => p.replace(/^\/+|\/+$/g, ""));

  return ["/w", clientSlug, ...cleanParts].join("/");
}

/**
 * Check if a pathname is a workspace route (under /w/ or /clients/).
 */
export function isWorkspaceRoute(pathname: string): boolean {
  return pathname.startsWith("/w/") || pathname.startsWith("/clients/");
}

/**
 * Check if a pathname is a platform route (not workspace-scoped).
 */
export function isPlatformRoute(pathname: string): boolean {
  const platformPrefixes = ["/dashboard", "/help", "/settings", "/admin"];
  return platformPrefixes.some((prefix) => pathname.startsWith(prefix));
}

/**
 * Extract the workspace section (tool) from a workspace path.
 *
 * @param pathname - The current URL pathname
 * @returns The tool section, or empty string if at workspace root
 */
export function extractWorkspaceSection(pathname: string): string {
  // Handle both /w/:slug/section and /clients/:slug/section
  const match = pathname.match(/\/(?:w|clients)\/[^/]+\/(.+)/);
  return match?.[1] || "";
}

/**
 * Replace the workspace slug in a path while preserving the section.
 */
export function replaceWorkspaceSlug(
  pathname: string,
  newClientSlug: string,
): string {
  return pathname
    .replace(/\/w\/[^/]+/, `/w/${newClientSlug}`)
    .replace(/\/clients\/[^/]+/, `/clients/${newClientSlug}`);
}
